import React, { useEffect, useMemo, useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import {
  CaretDownIcon,
  CheckCircleIcon,
  CircleIcon,
  ClockIcon,
  FileMagnifyingGlassIcon,
  ShieldIcon,
} from "@phosphor-icons/react";
import { MotiView } from "moti";
import { useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import AdminLayout from "../../layouts/AdminLayout.web";
import { useAppConfig } from "../../AppConfigProvider";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CourseInfoCard from "../../components/adminDashboard/CourseInfoCard/CourseInfoCard";
import AdminTable from "../../components/adminDashboard/AdminTable/AdminTable";
import AdminTablePagination from "../../components/adminDashboard/AdminTable/AdminTablePagination";
import FilterDropdown from "../../components/common/TableControls/FilterDropdown/FilterDropdown";
import CustomSearchInput from "../../components/common/general/CustomSearchInput/CustomSearchInput";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { UserRoleUI } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/Toast/ToastProvider";
import { useAuth } from "../../hooks/useAuth";
import useFileUpload, { type ProcessedFile } from "../../hooks/useFileUpload";
import Sentry from "../../utils/sentry";
import {
  ExternalCourse,
  RubricParseStatus,
  StoragePathType,
  type ActiveRubricSetQuery,
  type RubricSetParseStatusQuery,
  useActiveRubricSetQuery,
  useCreateRubricSetFromFileMutation,
  useListExternalCoursesQuery,
  useRubricSetParseStatusQuery,
} from "../../graphql/generated/graphql";
import type { RootStackParamList } from "../../navigation/navigation.types";
import RubricConfigSlideIn from "../../components/adminDashboard/CourseModernizer/RubricConfigSlideIn/RubricConfigSlideIn";

type RubricBadgeTone = "warning" | "success" | "processing" | "error";

interface UploadedRubricFile {
  fileId: string;
  name: string;
  size: number | null;
}

type ActiveRubricViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type ParsingRubricStatusViewModel = NonNullable<RubricSetParseStatusQuery["rubricSet"]["data"]>;

const rubricInProgressStatuses = [RubricParseStatus.Pending, RubricParseStatus.Parsing];

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong");

const getRubricNameFromFileName = (fileName: string) => fileName.replace(/\.[^/.]+$/, "").trim() || fileName;

function PageHeader({
  rubricBadgePrimaryText,
  rubricBadgeSecondaryText,
  rubricBadgeTone,
  onRubricBadgePress,
}: {
  rubricBadgePrimaryText: string;
  rubricBadgeSecondaryText: string;
  rubricBadgeTone: RubricBadgeTone;
  onRubricBadgePress: () => void;
}) {
  const isConfigured = rubricBadgeTone === "success";
  const badgeIconColor = isConfigured ? "#3E68FE" : rubricBadgeTone === "error" ? "#B42318" : "#B54708";
  const rubricBadgeToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricBadge
      : rubricBadgeTone === "error"
        ? styles.errorRubricBadge
        : rubricBadgeTone === "processing"
          ? styles.processingRubricBadge
          : styles.warningRubricBadge;
  const rubricPrimaryTextToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricPrimaryText
      : rubricBadgeTone === "error"
        ? styles.errorRubricText
        : rubricBadgeTone === "processing"
          ? styles.processingRubricText
          : styles.warningRubricText;
  const rubricSecondaryTextToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricSecondaryText
      : rubricBadgeTone === "error"
        ? styles.errorRubricText
        : rubricBadgeTone === "processing"
          ? styles.processingRubricText
          : styles.warningRubricText;

  return (
    <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }} style={styles.headerCard}>
      <CustomText
        text="Course Modernizer"
        size="m"
        textType="display"
        weight="ultraBold"
        style={styles.headerTitle}
        role="heading"
        aria-level="1"
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="s" />
      <Pressable
        role="button"
        accessibilityLabel={`${rubricBadgePrimaryText}. ${rubricBadgeSecondaryText}`}
        onPress={onRubricBadgePress}
        style={[styles.rubricBadge, rubricBadgeToneStyle]}>
        <ShieldIcon color={badgeIconColor} size={14} weight="bold" />
        <CustomText
          text={rubricBadgePrimaryText}
          size="s"
          weight="medium"
          style={[styles.rubricBadgePrimaryText, rubricPrimaryTextToneStyle]}
          useTranslationText={false}
        />
        <CustomText
          text={rubricBadgeSecondaryText}
          size="xs"
          weight="medium"
          style={[styles.rubricBadgeSecondaryText, rubricSecondaryTextToneStyle]}
          useTranslationText={false}
        />
        {isConfigured ? <CaretDownIcon color="#84ADFF" size={12} weight="bold" /> : null}
      </Pressable>
    </MotiView>
  );
}

type ModernizerFilter = "all" | "modernized" | "inProgress" | "notStarted";
type PlaceholderFilter = "all";

function CourseModernizerView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];
  const { userInfo } = useAuth();
  const { showToast } = useToast();
  const isAdmin = userInfo.userRole !== UserRoleUI.FACULTY;
  const [rubricSheetOpen, setRubricSheetOpen] = useState(false);
  const [selectedRubricFile, setSelectedRubricFile] = useState<UploadedRubricFile | null>(null);
  const [rubricUploadInProgress, setRubricUploadInProgress] = useState(false);
  const [rubricCreateInProgress, setRubricCreateInProgress] = useState(false);
  const [rubricUploadError, setRubricUploadError] = useState<string | null>(null);
  const [parsingRubricId, setParsingRubricId] = useState<string | null>(null);
  const [completedParsingRubricId, setCompletedParsingRubricId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ModernizerFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [facultyFilter, setFacultyFilter] = useState("all");
  const [facultyFilterDropdownOpen, setFacultyFilterDropdownOpen] = useState(false);
  const [termFilter, setTermFilter] = useState<PlaceholderFilter>("all");
  const [termFilterDropdownOpen, setTermFilterDropdownOpen] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState<PlaceholderFilter>("all");
  const [subjectFilterDropdownOpen, setSubjectFilterDropdownOpen] = useState(false);
  const [programFilter, setProgramFilter] = useState<PlaceholderFilter>("all");
  const [programFilterDropdownOpen, setProgramFilterDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    data: activeRubricData,
    loading: activeRubricLoading,
    refetch: refetchActiveRubric,
  } = useActiveRubricSetQuery({
    variables: { schoolId: schoolId ?? "" },
    skip: !schoolId,
  });

  const activeRubric = activeRubricData?.activeRubricSet?.data ?? null;

  const {
    data: parsingRubricData,
    loading: parsingRubricLoading,
    stopPolling: stopParsingRubricPolling,
  } = useRubricSetParseStatusQuery({
    variables: { id: parsingRubricId ?? "" },
    skip: !parsingRubricId,
    pollInterval: parsingRubricId ? 2000 : 0,
  });

  const parsingRubricStatus = parsingRubricData?.rubricSet?.data ?? null;
  const parsingRubricIsReady = parsingRubricStatus?.parseStatus === RubricParseStatus.Ready;
  const waitingForReadyRubric = parsingRubricIsReady && parsingRubricId != null && activeRubric?.id !== parsingRubricId;
  const displayedRubric: ActiveRubricViewModel | ParsingRubricStatusViewModel | null =
    parsingRubricStatus && (!parsingRubricIsReady || waitingForReadyRubric) ? parsingRubricStatus : activeRubric;
  const [createRubricSetFromFile] = useCreateRubricSetFromFileMutation();

  const uploadErrorHandler = (message?: string) => {
    const errorMessage = message || "Rubric upload failed";
    setRubricUploadError(errorMessage);
    showToast({ message: errorMessage, type: "error" });
  };

  const { handleFileUpload, allowedMimeTypes, maxFileSize } = useFileUpload({
    storagePath: StoragePathType.Rubrics,
    onError: uploadErrorHandler,
    multiple: false,
  });

  useEffect(() => {
    if (!parsingRubricId || !parsingRubricStatus?.parseStatus) {
      return;
    }

    if (parsingRubricStatus.parseStatus === RubricParseStatus.Ready) {
      stopParsingRubricPolling();
      void refetchActiveRubric();
      if (completedParsingRubricId !== parsingRubricId) {
        setCompletedParsingRubricId(parsingRubricId);
        showToast({ message: "Rubric parsed and activated", type: "success" });
      }
      return;
    }

    if (parsingRubricStatus.parseStatus === RubricParseStatus.Failed) {
      stopParsingRubricPolling();
      if (completedParsingRubricId !== parsingRubricId) {
        setCompletedParsingRubricId(parsingRubricId);
        showToast({ message: parsingRubricStatus.parseError || "Rubric parsing failed", type: "error" });
      }
    }
  }, [
    completedParsingRubricId,
    parsingRubricStatus,
    parsingRubricId,
    refetchActiveRubric,
    showToast,
    stopParsingRubricPolling,
  ]);

  const handleSelectRubricFile = async () => {
    setRubricUploadError(null);
    setRubricUploadInProgress(true);
    setParsingRubricId(null);
    setCompletedParsingRubricId(null);

    try {
      const processedFiles = await handleFileUpload();
      const processedFile: ProcessedFile | undefined = processedFiles?.[0];

      if (!processedFile) {
        return;
      }

      setSelectedRubricFile({
        fileId: processedFile.file.id,
        name: processedFile.localUploadedFile.name,
        size: processedFile.localUploadedFile.size ?? processedFile.size ?? null,
      });
    } catch (error) {
      const message = getErrorMessage(error);
      setRubricUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setRubricUploadInProgress(false);
    }
  };

  const handleParseRubric = async () => {
    if (!selectedRubricFile || !schoolId) {
      const message = schoolId ? "Select a rubric file first" : "School configuration is missing";
      setRubricUploadError(message);
      showToast({ message, type: "error" });
      return;
    }

    setRubricUploadError(null);
    setRubricCreateInProgress(true);

    try {
      const result = await createRubricSetFromFile({
        variables: {
          input: {
            schoolId,
            fileId: selectedRubricFile.fileId,
            name: getRubricNameFromFileName(selectedRubricFile.name),
            setActive: true,
          },
        },
      });

      const response = result.data?.createRubricSetFromFile;
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Failed to queue rubric parsing");
      }

      setParsingRubricId(response.data.id);
      setSelectedRubricFile(null);
      showToast({ message: "Rubric parsing started", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      setRubricUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setRubricCreateInProgress(false);
    }
  };

  const variables = {
    input: {
      page,
      pageSize,
      searchQuery: searchQuery.trim() || undefined,
      facultyId: isAdmin && facultyFilter !== "all" ? facultyFilter : undefined,
    },
  };

  const { data, loading } = useListExternalCoursesQuery({
    variables,
  });

  const courses = (data?.listExternalCourses?.data?.courses ?? []).filter((course): course is ExternalCourse =>
    Boolean(course)
  );

  const summary = useMemo(() => {
    const counts = { totalCourses: courses.length, modernized: 0, inProgress: 0, notStarted: 0 };
    counts.notStarted = courses.length;
    return counts;
  }, [courses]);

  const visibleBaseCourses = courses;

  const facultyOptions = useMemo(() => {
    const optionsMap = new Map<string, string>();
    courses.forEach((course) => {
      course.users?.forEach((faculty) => {
        if (faculty?.id && faculty.name) {
          optionsMap.set(faculty.id, faculty.name);
        }
      });
    });

    return [
      { label: "All Faculty", value: "all" },
      ...Array.from(optionsMap.entries()).map(([value, label]) => ({ label, value })),
    ];
  }, [courses]);

  const visibleCourses = useMemo(() => {
    if (activeFilter === "all" || activeFilter === "notStarted") {
      return visibleBaseCourses;
    }
    return [];
  }, [activeFilter, visibleBaseCourses]);

  const totalItems =
    activeFilter === "all" || activeFilter === "notStarted" ? (data?.listExternalCourses?.data?.totalCount ?? 0) : 0;

  const termItems = [{ label: "All Terms", value: "all" }];
  const subjectItems = [{ label: "All Subjects", value: "all" }];
  const programItems = [{ label: "All Programs", value: "all" }];
  const rubricStatus = displayedRubric?.parseStatus;
  const isRubricProcessing = rubricStatus != null && rubricInProgressStatuses.includes(rubricStatus);
  const rubricBadgeTone: RubricBadgeTone =
    rubricStatus === RubricParseStatus.Ready
      ? "success"
      : rubricStatus === RubricParseStatus.Failed
        ? "error"
        : isRubricProcessing
          ? "processing"
          : "warning";
  const rubricBadgePrimaryText =
    rubricStatus === RubricParseStatus.Ready
      ? (displayedRubric?.name ?? "Rubric configured")
      : rubricStatus === RubricParseStatus.Failed
        ? "Rubric needs attention"
        : isRubricProcessing
          ? "Rubric processing"
          : "No rubric configured";
  const rubricBadgeSecondaryText =
    rubricStatus === RubricParseStatus.Ready
      ? (displayedRubric?.detectedFrameworkType ?? "Active")
      : rubricStatus === RubricParseStatus.Failed
        ? "Review"
        : isRubricProcessing
          ? "Working"
          : "Set up";
  const rubricUploadState = rubricCreateInProgress
    ? "creating"
    : rubricUploadInProgress
      ? "uploading"
      : selectedRubricFile
        ? "uploaded"
        : "idle";

  return (
    <AdminLayout withPadding={false} isCourseModernizerPage={true}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <PageHeader
            rubricBadgePrimaryText={rubricBadgePrimaryText}
            rubricBadgeSecondaryText={rubricBadgeSecondaryText}
            rubricBadgeTone={rubricBadgeTone}
            onRubricBadgePress={() => setRubricSheetOpen(true)}
          />
          <CustomSpacing type="vertical" size="l" />

          <View style={styles.infoCardsContainer}>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<FileMagnifyingGlassIcon />}
                number={summary.totalCourses}
                title="Total Courses"
                iconBackgroundColor="rgb(239, 246, 255)"
                iconColor="rgb(37, 99, 235)"
                isActive={activeFilter === "all"}
                onPress={() => setActiveFilter("all")}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<CheckCircleIcon />}
                number={summary.modernized}
                title="Modernized"
                iconBackgroundColor="rgb(240, 253, 244)"
                iconColor="rgb(22, 163, 74)"
                isActive={activeFilter === "modernized"}
                onPress={() => setActiveFilter("modernized")}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<ClockIcon />}
                number={summary.inProgress}
                title="In Progress"
                iconBackgroundColor="rgb(255, 251, 235)"
                iconColor="rgb(217, 119, 6)"
                isActive={activeFilter === "inProgress"}
                onPress={() => setActiveFilter("inProgress")}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<CircleIcon />}
                number={summary.notStarted}
                title="Not Started"
                iconBackgroundColor="rgb(243, 244, 246)"
                iconColor="rgb(75, 85, 99)"
                isActive={activeFilter === "notStarted"}
                onPress={() => setActiveFilter("notStarted")}
              />
            </View>
          </View>

          <CustomSpacing type="vertical" size="l" />

          <CustomText
            text="External Courses"
            size="s"
            weight="medium"
            style={styles.headerThird}
            role="heading"
            aria-level="3"
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="l" />

          <AdminTable>
            <View style={styles.tableControlsRow}>
              <View style={styles.filtersRow}>
                {isAdmin && (
                  <FilterDropdown
                    filterValue={facultyFilter}
                    setFilterValue={(value) => {
                      setFacultyFilter(value);
                      setPage(1);
                    }}
                    filterDropdownOpen={facultyFilterDropdownOpen}
                    setFilterDropdownOpen={setFacultyFilterDropdownOpen}
                    filterItems={facultyOptions}
                    placeholder="All Faculty"
                  />
                )}
                <FilterDropdown
                  filterValue={termFilter}
                  setFilterValue={setTermFilter}
                  filterDropdownOpen={termFilterDropdownOpen}
                  setFilterDropdownOpen={setTermFilterDropdownOpen}
                  filterItems={termItems}
                  placeholder="All Terms"
                />
                <FilterDropdown
                  filterValue={subjectFilter}
                  setFilterValue={setSubjectFilter}
                  filterDropdownOpen={subjectFilterDropdownOpen}
                  setFilterDropdownOpen={setSubjectFilterDropdownOpen}
                  filterItems={subjectItems}
                  placeholder="All Subjects"
                />
                <FilterDropdown
                  filterValue={programFilter}
                  setFilterValue={setProgramFilter}
                  filterDropdownOpen={programFilterDropdownOpen}
                  setFilterDropdownOpen={setProgramFilterDropdownOpen}
                  filterItems={programItems}
                  placeholder="All Programs"
                />
              </View>
              <View style={styles.searchInputWrapper}>
                <CustomSearchInput
                  placeholder={isAdmin ? "Search courses, codes, or faculty" : "Search courses or codes"}
                  searchQuery={searchQuery}
                  setSearchQuery={(value) => {
                    setSearchQuery(value);
                    setPage(1);
                  }}
                />
              </View>
            </View>
            <View style={styles.tableHeaderRow}>
              <CustomText
                text="Course Name"
                size="xs"
                weight="medium"
                style={[styles.tableHeaderCell, styles.courseColumn]}
                useTranslationText={false}
              />
              <CustomText
                text="Term"
                size="xs"
                weight="medium"
                style={[styles.tableHeaderCell, styles.termColumn]}
                useTranslationText={false}
              />
              <CustomText
                text="Faculty"
                size="xs"
                weight="medium"
                style={[styles.tableHeaderCell, styles.facultyColumn]}
                useTranslationText={false}
              />
              <CustomText
                text="Status"
                size="xs"
                weight="medium"
                style={[styles.tableHeaderCell, styles.statusColumn]}
                useTranslationText={false}
              />
              <CustomText
                text="Score"
                size="xs"
                weight="medium"
                style={[styles.tableHeaderCell, styles.scoreColumn]}
                useTranslationText={false}
              />
              <CustomText
                text="Actions"
                size="xs"
                weight="medium"
                style={[styles.tableHeaderCell, styles.actionsColumn]}
                useTranslationText={false}
              />
            </View>

            {loading ? (
              <View style={styles.emptyState}>
                <CustomText text="Loading external courses..." size="s" useTranslationText={false} />
              </View>
            ) : visibleCourses.length === 0 ? (
              <View style={styles.emptyState}>
                <CustomText text="No external courses found" size="s" useTranslationText={false} />
              </View>
            ) : (
              visibleCourses.map((course) => (
                <View key={course.id} style={styles.tableRow}>
                  <View style={[styles.tableCell, styles.courseColumn]}>
                    <CustomText
                      text={course.name || "Untitled course"}
                      size="s"
                      weight="medium"
                      useTranslationText={false}
                    />
                    <CustomText
                      text={course.courseCode || course.providerCourseId || "—"}
                      size="xs"
                      style={styles.secondaryText}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={[styles.tableCell, styles.termColumn]}>
                    <CustomText text="-" size="s" useTranslationText={false} />
                  </View>
                  <View style={[styles.tableCell, styles.facultyColumn]}>
                    <CustomText
                      text={
                        course.users
                          ?.map((faculty) => faculty?.name)
                          .filter(Boolean)
                          .join(", ") || "-"
                      }
                      size="s"
                      useTranslationText={false}
                    />
                  </View>
                  <View style={[styles.tableCell, styles.statusColumn]}>
                    <CustomText text="Not Started" size="s" useTranslationText={false} />
                  </View>
                  <View style={[styles.tableCell, styles.scoreColumn]}>
                    <CustomText text="-" size="s" useTranslationText={false} />
                  </View>
                  <View style={[styles.tableCell, styles.actionsColumn]}>
                    <View style={styles.rowActions}>
                      <CustomButton
                        text="Re-run"
                        styleType="lightStroke"
                        size="s"
                        useTranslationText={false}
                        onPress={() => {}}
                      />
                      <CustomButton
                        text="View Results"
                        styleType="primaryTransparentStroke"
                        size="s"
                        useTranslationText={false}
                        onPress={() => navigation.navigate("Course Modernizer Review", { courseId: course.id })}
                      />
                    </View>
                  </View>
                </View>
              ))
            )}
          </AdminTable>

          {!loading && totalItems > 0 && (
            <AdminTablePagination
              currentPage={page}
              totalItems={totalItems}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onItemsPerPageChange={(value) => {
                setPageSize(value);
                setPage(1);
              }}
              navigationStyle="icons"
              containerStyle={styles.pagination}
            />
          )}
        </View>
        <RubricConfigSlideIn
          visible={rubricSheetOpen}
          onClose={() => setRubricSheetOpen(false)}
          rubric={displayedRubric}
          loadingRubric={
            activeRubricLoading || waitingForReadyRubric || (parsingRubricLoading && displayedRubric == null)
          }
          selectedFileName={selectedRubricFile?.name ?? null}
          selectedFileSize={selectedRubricFile?.size ?? null}
          uploadState={rubricUploadState}
          uploadError={rubricUploadError}
          allowedMimeTypes={allowedMimeTypes}
          maxFileSize={maxFileSize}
          onSelectFile={handleSelectRubricFile}
          onClearFile={() => {
            setSelectedRubricFile(null);
            setRubricUploadError(null);
          }}
          onParseRubric={handleParseRubric}
        />
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  infoCardsContainer: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  },
  infoCardWrapper: {
    flex: 1,
    minWidth: 220,
  },
  headerThird: {
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  headerCard: {
    paddingVertical: 6,
    paddingHorizontal: 0,
    width: "100%",
  },
  headerTitle: {
    color: "#101828",
    fontSize: 24,
    lineHeight: 32,
  },
  rubricBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 12,
    gap: 8,
  },
  warningRubricBadge: {
    backgroundColor: "#FFFAEB",
    borderColor: "#FEDF89",
  },
  configuredRubricBadge: {
    backgroundColor: "#F5F8FF",
    borderColor: "#D1E0FF",
  },
  processingRubricBadge: {
    backgroundColor: "#FFFAEB",
    borderColor: "#FEDF89",
  },
  errorRubricBadge: {
    backgroundColor: "#FEF3F2",
    borderColor: "#FECDCA",
  },
  rubricBadgePrimaryText: {
    lineHeight: 20,
  },
  rubricBadgeSecondaryText: {
    lineHeight: 18,
  },
  warningRubricText: {
    color: "#B54708",
  },
  configuredRubricPrimaryText: {
    color: "#175CD3",
  },
  configuredRubricSecondaryText: {
    color: "#3E68FE",
  },
  processingRubricText: {
    color: "#B54708",
  },
  errorRubricText: {
    color: "#B42318",
  },
  tableControlsRow: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
  },
  filtersRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    flex: 1,
  },
  searchInputWrapper: {
    width: 320,
    maxWidth: "40%",
    minWidth: 260,
  },
  tableHeaderRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 10,
  },
  tableHeaderCell: {
    color: "#475467",
  },
  tableCell: {
    justifyContent: "center",
  },
  courseColumn: {
    flex: 2.4,
  },
  termColumn: {
    flex: 1.2,
  },
  facultyColumn: {
    flex: 1.8,
  },
  statusColumn: {
    flex: 1.1,
  },
  scoreColumn: {
    flex: 0.8,
  },
  actionsColumn: {
    flex: 1.7,
  },
  secondaryText: {
    color: "#667085",
  },
  rowActions: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
  emptyState: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  pagination: {
    marginTop: 16,
    borderTopWidth: 0,
  },
});

export default CourseModernizerView;
